import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, UNSAFE_NavigationContext } from "react-router-dom";

interface UseNavigationGuardOptions {
	when: boolean;
	onConfirm?: () => void;
}

export const useNavigationGuard = ({ when, onConfirm }: UseNavigationGuardOptions) => {
	const navigate = useNavigate();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isLeaving, setIsLeaving] = useState(false);
	const isActiveRef = useRef(false);
	const pendingTxRef = useRef<any>(null);
	const routerContext = useContext(UNSAFE_NavigationContext) as any;
	const isHandlingBackRef = useRef(false);

	const handlePopState = useCallback(() => {
		if (!isActiveRef.current) return;
		if (isHandlingBackRef.current) return;

		// Restore the current page in the history stack before showing the dialog.
		// All browsers: user pressed back → browser moved to prev-page.
		// go(1) brings us back to detail-page so that a single go(-1) on confirm reaches prev-page.
		isHandlingBackRef.current = true;
		try { window.history.go(1); } catch {}
		setTimeout(() => {
			setIsDialogOpen(true);
			isHandlingBackRef.current = false;
		}, 50);
	}, []);

	const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
		if (!isActiveRef.current) return;
		e.preventDefault();
		e.returnValue = "";
		return "";
	}, []);

	useEffect(() => {
		isActiveRef.current = when;
		if (when) {
			window.addEventListener("popstate", handlePopState);
			window.addEventListener("beforeunload", handleBeforeUnload);
			let unblock: (() => void) | null = null;
			try {
				const navigator = routerContext?.navigator;
				if (navigator && typeof navigator.block === "function") {
					unblock = navigator.block((tx: any) => {
						if (!isActiveRef.current) {
							tx.retry();
							return;
						}
						pendingTxRef.current = tx;
						setIsDialogOpen(true);
					});
				}
			} catch {}
			return () => {
				window.removeEventListener("popstate", handlePopState);
				window.removeEventListener("beforeunload", handleBeforeUnload);
				if (unblock) {
					try { unblock(); } catch {}
				}
			};
		} else {
			setIsDialogOpen(false);
		}
	}, [when, handlePopState, handleBeforeUnload, routerContext]);

	const cancelNavigation = useCallback(() => {
		setIsDialogOpen(false);
	}, []);

	const confirmNavigation = useCallback(() => {
		setIsLeaving(true);
		isActiveRef.current = false;
		onConfirm && onConfirm();

		// Handle router-blocked navigation (link clicks, programmatic navigate)
		const tx = pendingTxRef.current;
		if (tx) {
			pendingTxRef.current = null;
			try { tx.retry(); return; } catch {}
		}

		// Handle back-button navigation.
		// go(1) in handlePopState restored us to detail-page, so one step back reaches prev-page.
		try { window.history.go(-1); } catch { navigate(-1); }
	}, [navigate, onConfirm]);

	return {
		isDialogOpen,
		isLeaving,
		confirmNavigation,
		cancelNavigation,
	};
};

export default useNavigationGuard;


